// Warehouse Management Platform - Constants

export const ROLES = {
  ADMIN: 'ADMIN',
  HOST: 'HOST',
  TENANT: 'TENANT',
  STAFF: 'STAFF',
  LOGISTICS_PARTNER: 'LOGISTICS_PARTNER',
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Administrator',
  [ROLES.HOST]: 'Warehouse Partner',
  [ROLES.TENANT]: 'Storage Customer',
  [ROLES.STAFF]: 'Warehouse Staff',
  [ROLES.LOGISTICS_PARTNER]: 'Logistics Partner',
};

export const BOOKING_STATUS = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  AWAITING_HOST_APPROVAL: 'AWAITING_HOST_APPROVAL',
  APPROVED: 'APPROVED',
  CONFIRMED: 'CONFIRMED',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
};

export const BOOKING_STATUS_LABELS = {
  [BOOKING_STATUS.DRAFT]: 'Draft',
  [BOOKING_STATUS.PENDING]: 'Pending',
  [BOOKING_STATUS.AWAITING_HOST_APPROVAL]: 'Awaiting Approval',
  [BOOKING_STATUS.APPROVED]: 'Approved',
  [BOOKING_STATUS.CONFIRMED]: 'Confirmed',
  [BOOKING_STATUS.ACTIVE]: 'Active',
  [BOOKING_STATUS.COMPLETED]: 'Completed',
  [BOOKING_STATUS.CANCELLED]: 'Cancelled',
  [BOOKING_STATUS.REJECTED]: 'Rejected',
  [BOOKING_STATUS.EXPIRED]: 'Expired',
};

export const BOOKING_STATUS_COLORS = {
  [BOOKING_STATUS.DRAFT]: 'secondary',
  [BOOKING_STATUS.PENDING]: 'warning',
  [BOOKING_STATUS.AWAITING_HOST_APPROVAL]: 'info',
  [BOOKING_STATUS.APPROVED]: 'primary',
  [BOOKING_STATUS.CONFIRMED]: 'success',
  [BOOKING_STATUS.ACTIVE]: 'success',
  [BOOKING_STATUS.COMPLETED]: 'dark',
  [BOOKING_STATUS.CANCELLED]: 'danger',
  [BOOKING_STATUS.REJECTED]: 'danger',
  [BOOKING_STATUS.EXPIRED]: 'secondary',
};

export const LISTING_STATUS = {
  DRAFT: 'DRAFT',
  PENDING_REVIEW: 'PENDING_REVIEW',
  APPROVED: 'APPROVED',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  REJECTED: 'REJECTED',
  ON_HOLD: 'ON_HOLD',
  NEEDS_MORE_INFO: 'NEEDS_MORE_INFO',
  SUSPENDED: 'SUSPENDED',
};

export const LISTING_STATUS_LABELS = {
  [LISTING_STATUS.DRAFT]: 'Draft',
  [LISTING_STATUS.PENDING_REVIEW]: 'Pending Review',
  [LISTING_STATUS.APPROVED]: 'Approved',
  [LISTING_STATUS.ACTIVE]: 'Active',
  [LISTING_STATUS.INACTIVE]: 'Inactive',
  [LISTING_STATUS.REJECTED]: 'Rejected',
  [LISTING_STATUS.ON_HOLD]: 'On Hold',
  [LISTING_STATUS.NEEDS_MORE_INFO]: 'Needs Info',
  [LISTING_STATUS.SUSPENDED]: 'Suspended',
};

export const LISTING_STATUS_COLORS = {
  [LISTING_STATUS.DRAFT]: 'secondary',
  [LISTING_STATUS.PENDING_REVIEW]: 'warning',
  [LISTING_STATUS.APPROVED]: 'info',
  [LISTING_STATUS.ACTIVE]: 'success',
  [LISTING_STATUS.INACTIVE]: 'dark',
  [LISTING_STATUS.REJECTED]: 'danger',
  [LISTING_STATUS.ON_HOLD]: 'warning',
  [LISTING_STATUS.NEEDS_MORE_INFO]: 'info',
  [LISTING_STATUS.SUSPENDED]: 'danger',
};

export const INVOICE_STATUS = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  SENT: 'SENT',
  PAID: 'PAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  OVERDUE: 'OVERDUE',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
};

export const INVOICE_STATUS_COLORS = {
  [INVOICE_STATUS.DRAFT]: 'secondary',
  [INVOICE_STATUS.PENDING]: 'warning',
  [INVOICE_STATUS.SENT]: 'info',
  [INVOICE_STATUS.PAID]: 'success',
  [INVOICE_STATUS.PARTIALLY_PAID]: 'primary',
  [INVOICE_STATUS.OVERDUE]: 'danger',
  [INVOICE_STATUS.CANCELLED]: 'dark',
  [INVOICE_STATUS.REFUNDED]: 'warning',
};

export const GRN_STATUS = {
  PENDING: 'PENDING',
  QC_IN_PROGRESS: 'QC_IN_PROGRESS',
  QC_PASSED: 'QC_PASSED',
  QC_FAILED: 'QC_FAILED',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
  PUTAWAY_COMPLETE: 'PUTAWAY_COMPLETE',
};

export const DISPATCH_STATUS = {
  PENDING: 'PENDING',
  PICKING: 'PICKING',
  PACKED: 'PACKED',
  READY_FOR_DISPATCH: 'READY_FOR_DISPATCH',
  DISPATCHED: 'DISPATCHED',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
};

export const TASK_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  ON_HOLD: 'ON_HOLD',
};

export const DISPUTE_STATUS = {
  OPEN: 'OPEN',
  UNDER_REVIEW: 'UNDER_REVIEW',
  ESCALATED: 'ESCALATED',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
};

export const DISPUTE_STATUS_COLORS = {
  [DISPUTE_STATUS.OPEN]: 'warning',
  [DISPUTE_STATUS.UNDER_REVIEW]: 'info',
  [DISPUTE_STATUS.ESCALATED]: 'danger',
  [DISPUTE_STATUS.RESOLVED]: 'success',
  [DISPUTE_STATUS.CLOSED]: 'secondary',
};

export const KYC_STATUS = {
  NOT_SUBMITTED: 'NOT_SUBMITTED',
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
};

export const KYC_STATUS_COLORS = {
  [KYC_STATUS.NOT_SUBMITTED]: 'secondary',
  [KYC_STATUS.PENDING]: 'warning',
  [KYC_STATUS.VERIFIED]: 'success',
  [KYC_STATUS.REJECTED]: 'danger',
};

// Values must match backend CommodityType enum
// (Backend/marketplace-service/app/domain/value_objects/commodity_type.py)
export const COMMODITY_TYPES = [
  { value: 'DRY_GOODS', label: 'Dry Goods' },
  { value: 'REFRIGERATED', label: 'Refrigerated' },
  { value: 'FROZEN', label: 'Frozen' },
  { value: 'HAZMAT', label: 'Hazardous Materials' },
  { value: 'PHARMA', label: 'Pharmaceutical' },
  { value: 'AUTO_PARTS', label: 'Auto Parts' },
  { value: 'ECOMMERCE_RETURNS', label: 'E-Commerce Returns' },
];

export const WAREHOUSE_TYPES = [
  { value: 'GENERAL', label: 'General / Dry' },
  { value: 'COLD_STORAGE', label: 'Cold Storage' },
  { value: 'BONDED', label: 'Bonded Warehouse' },
  { value: 'PHARMA', label: 'Pharma' },
  { value: 'ECOMMERCE', label: 'E-Commerce / Fulfillment' },
  { value: 'GARAGE', label: 'Garage' },
  { value: 'HOUSE_SPACE', label: 'House Space' },
];

// Fallback list for the "Commodity Type (NOT) Accepted" dropdown
// in the listing wizard. Mirrors the seed in
// migrations/.../20260517_0001_non_accepted_commodities.py so the UI
// stays usable when the public master-data endpoint is unreachable.
export const NON_ACCEPTED_COMMODITIES = [
  { value: 'HAZMAT',           label: 'Hazardous Materials' },
  { value: 'EXPLOSIVES',       label: 'Explosives' },
  { value: 'RADIOACTIVE',      label: 'Radioactive Materials' },
  { value: 'CONTROLLED_DRUGS', label: 'Controlled Drugs' },
  { value: 'LIVE_ANIMALS',     label: 'Live Animals' },
  { value: 'PERISHABLE_FOOD',  label: 'Perishable Food' },
  { value: 'FROZEN_GOODS',     label: 'Frozen Goods' },
  { value: 'WET_GOODS',        label: 'Wet / Liquid Bulk' },
  { value: 'ODOROUS_GOODS',    label: 'Odorous Goods' },
  { value: 'TOBACCO',          label: 'Tobacco Products' },
  { value: 'ALCOHOL',          label: 'Alcohol & Spirits' },
  { value: 'FIREARMS',         label: 'Firearms & Ammunition' },
];

export const AMENITIES = [
  { value: 'FIRE_SUPPRESSION', label: 'Fire Suppression' },
  { value: 'CCTV', label: 'CCTV Surveillance' },
  { value: 'WEIGHBRIDGE', label: 'Weighbridge' },
  { value: 'FUMIGATION', label: 'Fumigation' },
  { value: 'EV_CHARGING', label: 'EV Charging' },
  { value: 'SECURITY_GUARD', label: '24/7 Security' },
  { value: 'LOADING_DOCK', label: 'Loading Dock' },
  { value: 'FORKLIFT', label: 'Forklift Available' },
];

export const CERTIFICATIONS = [
  { value: 'FSSAI', label: 'FSSAI' },
  { value: 'ISO_22000', label: 'ISO 22000' },
  { value: 'PHARMA_LICENSE', label: 'Pharma License' },
  { value: 'HAZMAT_CERT', label: 'Hazmat Certification' },
];

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
  'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
  'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra',
  'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Chandigarh', 'Puducherry',
];

// GST state codes — the first two characters of every GSTIN. Used
// across Contract Defaults (place-of-supply), E-way Bill Templates
// (from_state_code), and any future GST-aware surface. Sorted by
// state name so dropdowns render alphabetically. Leading zeros are
// preserved as strings (Jammu & Kashmir = "01", Punjab = "03", etc.).
export const INDIAN_GST_STATES = [
  { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '18', name: 'Assam' },
  { code: '10', name: 'Bihar' },
  { code: '04', name: 'Chandigarh' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '26', name: 'Dadra & Nagar Haveli and Daman & Diu' },
  { code: '07', name: 'Delhi' },
  { code: '30', name: 'Goa' },
  { code: '24', name: 'Gujarat' },
  { code: '06', name: 'Haryana' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '01', name: 'Jammu and Kashmir' },
  { code: '20', name: 'Jharkhand' },
  { code: '29', name: 'Karnataka' },
  { code: '32', name: 'Kerala' },
  { code: '31', name: 'Lakshadweep' },
  { code: '38', name: 'Ladakh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '27', name: 'Maharashtra' },
  { code: '14', name: 'Manipur' },
  { code: '17', name: 'Meghalaya' },
  { code: '15', name: 'Mizoram' },
  { code: '13', name: 'Nagaland' },
  { code: '21', name: 'Odisha' },
  { code: '34', name: 'Puducherry' },
  { code: '03', name: 'Punjab' },
  { code: '08', name: 'Rajasthan' },
  { code: '11', name: 'Sikkim' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '36', name: 'Telangana' },
  { code: '16', name: 'Tripura' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '19', name: 'West Bengal' },
];

// Helper for "27 — Maharashtra" style dropdown labels.
export const gstStateLabel = ({ code, name }) => `${code} — ${name}`;

export const SPACE_TYPES = [
  { value: 'FULL_WAREHOUSE', label: 'Full Warehouse' },
  { value: 'PARTIAL_BAY', label: 'Partial Bay' },
  { value: 'PALLET_LEVEL', label: 'Pallet-level' },
  { value: 'RACK_LEVEL', label: 'Rack-level' },
  { value: 'GARAGE_ROOM', label: 'Garage / Room' },
  { value: 'FLAT', label: 'Flat / Apartment' },
];

export const BOOKING_MODES = [
  { value: 'INSTANT', label: 'Instant book' },
  { value: 'REQUEST_TO_BOOK', label: 'Request to book' },
  { value: 'BOTH', label: 'Both (instant + request)' },
];

export const DURATION_TYPES = [
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
];

// Pricing-model fallback — used when the public master-data endpoint
// is unreachable. The ``unit`` + ``period`` fields drive the
// "₹X per {unit} per {period}" label format and the period-aware
// billing math (see Backend/marketplace-service pricing_unit_math.py).
export const PRICING_MODELS = [
  {
    value: 'PER_SQFT_MONTH', label: 'Per Sq.Ft / Month',
    unit: 'SQFT', period: 'MONTHLY',
  },
  {
    value: 'PER_SQFT_DAY', label: 'Per Sq.Ft / Day',
    unit: 'SQFT', period: 'DAILY',
  },
  {
    value: 'PER_SQFT_YEAR', label: 'Per Sq.Ft / Year',
    unit: 'SQFT', period: 'YEARLY',
  },
  {
    value: 'PER_PALLET_MONTH', label: 'Per Pallet / Month',
    unit: 'PALLET', period: 'MONTHLY',
  },
  {
    value: 'PER_PALLET_DAY', label: 'Per Pallet / Day',
    unit: 'PALLET', period: 'DAILY',
  },
  {
    value: 'PER_CBM_MONTH', label: 'Per CBM / Month',
    unit: 'CBM', period: 'MONTHLY',
  },
  {
    value: 'PER_TON_MONTH', label: 'Per Ton / Month',
    unit: 'MT', period: 'MONTHLY',
  },
  {
    value: 'FIXED_MONTHLY', label: 'Fixed Monthly Rate',
    unit: 'FIXED', period: 'MONTHLY',
  },
];

export const PAYMENT_METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer (NEFT/RTGS)' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'RAZORPAY', label: 'Razorpay (Online)' },
];

export const GST_RATES = [
  { value: 0, label: 'Exempt (0%)' },
  { value: 5, label: '5% GST' },
  { value: 12, label: '12% GST' },
  { value: 18, label: '18% GST' },
  { value: 28, label: '28% GST' },
];

export const SHIPMENT_STATUS = {
  BOOKED: 'BOOKED',
  PICKED_UP: 'PICKED_UP',
  IN_TRANSIT: 'IN_TRANSIT',
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
};

export const SHIPMENT_STATUS_COLORS = {
  [SHIPMENT_STATUS.BOOKED]: 'info',
  [SHIPMENT_STATUS.PICKED_UP]: 'primary',
  [SHIPMENT_STATUS.IN_TRANSIT]: 'warning',
  [SHIPMENT_STATUS.OUT_FOR_DELIVERY]: 'info',
  [SHIPMENT_STATUS.DELIVERED]: 'success',
  [SHIPMENT_STATUS.FAILED]: 'danger',
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

export const DATE_FORMAT = 'dd MMM yyyy';
export const DATETIME_FORMAT = 'dd MMM yyyy, hh:mm a';
export const API_DATE_FORMAT = 'yyyy-MM-dd';
